import { CopilotPopup, CopilotSidebar } from '@copilotkit/react-ui';
import React, { useEffect, useState, useRef } from 'react';
import { SidebarHeader } from '@features/sidebar/components/sidebar-header/SidebarHeader';
import {
  CHAT_INSTRUCTIONS,
  SIDEBAR_LABELS,
  SCREENS,
} from '@features/sidebar/constants';
import { SidebarIcons } from '@features/sidebar/components/sidebar-icons/SidebarIcons';
import { AssistantMessage } from '@features/sidebar/components/assistant-message/AssistantMessage';
import { UserMessage } from '@features/sidebar/components/user-message/UserMessage';
import '@features/sidebar/Sidebar.scss';
import { ToggleButton } from '@features/sidebar/components/toggle-button/ToggleButton';
import { useCopilotMessagesContext } from '@copilotkit/react-core';
import { Role, TextMessage } from '@copilotkit/runtime-client-gql';
import { SidebarInput } from '@features/sidebar/components/sidebar-input/SidebarInput';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useAI } from 'src/hooks/useAI';

import { AIService } from 'src/services/ai';
import { useAuthStore } from 'src/store/auth';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { cn } from '@utils/index';
import { useInsuranceStore } from 'src/store/insurance';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';

export const Sidebar = ({
  wrapperPopupClassName,
  wrapperSidebarClassName,
}: {
  wrapperPopupClassName?: string;
  wrapperSidebarClassName?: string;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { currentStep, data } = useOnboardingStore();
  const { chatHistory } = useAI();
  const { user } = useAuthStore();
  const plans = useInsuranceStore((state) => state.plans);
  const [labels, setLabels] = useState<typeof SIDEBAR_LABELS>(SIDEBAR_LABELS);
  const [sidebarIntroShown, setSidebarIntroShown] = useState<boolean>(() => {
    return localStorage.getItem('sidebarIntroShown') === '1';
  });
  const hasInitialized = useRef(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < SCREENS.xl);
    function handleResize() {
      const isMobile = window.innerWidth < SCREENS.xl;
      if (isMobile) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }
    window.addEventListener('resize', handleResize);
  }, []);

  // Fetch dynamic labels from AI initialize (only once when user.firstName is available)
  useEffect(() => {
    if (!user?.firstName || hasInitialized.current) {
      return;
    }

    (async () => {
      try {
        hasInitialized.current = true;
        setIsLoading(true);
        const applicationData = transformApplicationData(data, plans);
        const applicationDTO = btoa(JSON.stringify(applicationData));

        const response = await AIService.initialize({
          userId: String(user?.id || ''),
          context: {
            page: 'sidebar',
            applicationDTO,
            name: {
              first_name: user?.firstName || '',
              last_name: user?.lastName || '',
            },
          },
        });
        if (response?.answer) {
          setLabels({ ...SIDEBAR_LABELS, initial: response.answer });
        }
      } catch (e) {
        // silent fallback to default labels
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.firstName, data, plans, user?.id, user?.lastName]);

  // Mark intro animation as shown on first mount
  useEffect(() => {
    if (!sidebarIntroShown) {
      setSidebarIntroShown(true);
      localStorage.setItem('sidebarIntroShown', '1');
    }
  }, [sidebarIntroShown]);

  const { setMessages: setCopilotMessages } = useCopilotMessagesContext();

  useEffect(() => {
    if (chatHistory && chatHistory.length > 0) {
      // Sync the chat history with CopilotKit whenever it changes
      const messages = chatHistory
        .filter((chatItem) => chatItem.message && chatItem.answer) // Filter out items with empty content
        .map((chatItem) => {
          // Create user message
          const userMessage = new TextMessage({
            content: chatItem.message,
            role: Role.User,
          });

          // Create assistant message
          const assistantMessage = new TextMessage({
            content: chatItem.answer,
            role: Role.Assistant,
          });

          return [userMessage, assistantMessage];
        })
        .flat();

      setCopilotMessages(messages);
    } else if (chatHistory.length === 0) {
      // Clear messages when chat history is empty
      setCopilotMessages([]);
    }
  }, [chatHistory, setCopilotMessages]);

  if (isLoading) {
    return <div>
      <AnimatedDots />
    </div>;
  }

  return (
    <>
      {isMobile ? (
        <CopilotPopup
          className={cn(wrapperPopupClassName, 'assistant-popup')}
          clickOutsideToClose={false}
          hitEscapeToClose={false}
          Header={SidebarHeader}
          labels={labels}
          icons={SidebarIcons}
          Input={SidebarInput}
          instructions={CHAT_INSTRUCTIONS}
          AssistantMessage={AssistantMessage}
          UserMessage={UserMessage}
          Button={ToggleButton}
        />
      ) : (
        <CopilotSidebar
          className={cn(
            wrapperSidebarClassName,
            'assistant-sidebar h-full',
            !currentStep && 'firstscreen'
          )}
          defaultOpen={true}
          clickOutsideToClose={false}
          hitEscapeToClose={false}
          Header={SidebarHeader}
          labels={labels}
          icons={SidebarIcons}
          Input={SidebarInput}
          instructions={CHAT_INSTRUCTIONS}
          AssistantMessage={AssistantMessage}
          UserMessage={UserMessage}
        />
      )}
    </>
  );
};
